import * as React from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    MenuItem,
    Box,
    Typography,
    Paper,
    Button,
    styled,
} from "@mui/material";
import { ICsvColumnMapping } from "../../../../../../helpers/bulk_helpers";
import { mapCsvDataToBulkFormData } from "../../../../../../helpers/bulk_helpers";
import { IBulkFormData } from "../../../../../../config/bulk";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
}));

interface IColumnMappingTableProps {
    csvData: string[][];
    onMappingChange(mapping: ICsvColumnMapping): void;
    onConfirm(mappedData: IBulkFormData[]): void;
    onCancel(): void;
}

interface IFieldOption {
    value: keyof ICsvColumnMapping | '';
    label: string;
    required?: boolean;
}

const FIELD_OPTIONS: IFieldOption[] = [
    { value: '', label: '-- Skip --' },
    { value: 'date', label: 'Date', required: true },
    { value: 'currency', label: 'Currency' },
    { value: 'amount', label: 'Amount', required: true },
    { value: 'description', label: 'Description' },
    { value: 'paymentMethod', label: 'Payment Method' },
    { value: 'categoryId', label: 'Category ID' },
    { value: 'location', label: 'Location' },
];

export const ColumnMappingTable = ({
    csvData,
    onMappingChange,
    onConfirm,
    onCancel,
}: IColumnMappingTableProps) => {
    const { t } = useTranslation();
    const [mapping, setMapping] = useState<ICsvColumnMapping>({});
    const [previewData, setPreviewData] = useState<IBulkFormData[]>([]);

    const headers = csvData[0] || [];
    const sampleRows = csvData.slice(1, 4); // Show first 3 rows as preview

    // Auto-detect mappings based on column names (case-insensitive)
    useEffect(() => {
        const autoMapping: ICsvColumnMapping = {};
        
        headers.forEach((header) => {
            const lowerHeader = header.toLowerCase().trim();
            
            // Try to match common column name patterns
            if (lowerHeader.includes('date') || lowerHeader.includes('datum') || lowerHeader.match(/\bat\b/i)) {
                if (!autoMapping.date) autoMapping.date = header;
            } else if (lowerHeader.includes('currency') || lowerHeader.includes('curr') || lowerHeader.includes('währung')) {
                if (!autoMapping.currency) autoMapping.currency = header;
            } else if (lowerHeader.includes('amount') || lowerHeader.includes('betrag') || lowerHeader.includes('value') || lowerHeader.includes('sum')) {
                if (!autoMapping.amount) autoMapping.amount = header;
            } else if (lowerHeader.includes('description') || lowerHeader.includes('desc') || lowerHeader.includes('beschreibung') || lowerHeader.includes('memo') || lowerHeader.includes('note')) {
                if (!autoMapping.description) autoMapping.description = header;
            } else if (lowerHeader.includes('payment') || lowerHeader.includes('zahlungsmethode')) {
                if (!autoMapping.paymentMethod) autoMapping.paymentMethod = header;
            } else if (lowerHeader.includes('category') || lowerHeader.includes('kategorie')) {
                if (!autoMapping.categoryId) autoMapping.categoryId = header;
            } else if (lowerHeader.includes('location') || lowerHeader.includes('ort') || lowerHeader.includes('place')) {
                if (!autoMapping.location) autoMapping.location = header;
            }
        });

        setMapping(autoMapping);
        onMappingChange(autoMapping);
    }, [headers.join(',')]);

    // Update preview when mapping changes
    useEffect(() => {
        if (Object.keys(mapping).length > 0) {
            try {
                const mapped = mapCsvDataToBulkFormData(csvData, mapping);
                setPreviewData(mapped.slice(0, 3)); // Preview first 3 rows
            } catch (error) {
                console.error('Error mapping data:', error);
                setPreviewData([]);
            }
        }
    }, [mapping, csvData]);

    const handleFieldChange = (csvColumn: string, field: keyof ICsvColumnMapping | '') => {
        const newMapping = { ...mapping };

        // Remove old mapping for this field if it exists
        Object.keys(newMapping).forEach((key) => {
            if (newMapping[key as keyof ICsvColumnMapping] === csvColumn) {
                delete newMapping[key as keyof ICsvColumnMapping];
            }
        });

        // Add new mapping
        if (field) {
            newMapping[field] = csvColumn;
        }

        setMapping(newMapping);
        onMappingChange(newMapping);
    };

    const getMappedField = (csvColumn: string): keyof ICsvColumnMapping | '' => {
        const entry = Object.entries(mapping).find(([_, value]) => value === csvColumn);
        return entry ? (entry[0] as keyof ICsvColumnMapping) : '';
    };

    const handleConfirm = () => {
        const mapped = mapCsvDataToBulkFormData(csvData, mapping);
        onConfirm(mapped);
    };

    const isRequiredFieldMapped = (field: keyof ICsvColumnMapping): boolean => {
        return !!mapping[field];
    };

    const requiredFieldsMapped = ['date', 'amount'].every(
        (field) => isRequiredFieldMapped(field as keyof ICsvColumnMapping)
    );

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Map CSV Columns to Fields
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                Select which CSV column corresponds to each field. Required fields are marked with *.
            </Typography>

            <StyledTableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>CSV Column</TableCell>
                            <TableCell>Sample Data</TableCell>
                            <TableCell>Map to Field</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {headers.map((header, index) => {
                            const sampleValue = sampleRows[0]?.[index] || '';
                            const mappedField = getMappedField(header);
                            const fieldOption = FIELD_OPTIONS.find((opt) => opt.value === mappedField);

                            return (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {header}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                maxWidth: 200,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {sampleValue || '(empty)'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={mappedField}
                                            onChange={(e) =>
                                                handleFieldChange(header, e.target.value as keyof ICsvColumnMapping | '')
                                            }
                                            variant="outlined"
                                        >
                                            {FIELD_OPTIONS.map((option) => {
                                                const isAlreadyMapped =
                                                    option.value &&
                                                    option.value !== mappedField &&
                                                    mapping[option.value as keyof ICsvColumnMapping];

                                                return (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                        // disabled={isAlreadyMapped}
                                                    >
                                                        {option.label}
                                                        {option.required && ' *'}
                                                        {isAlreadyMapped && ' (already mapped)'}
                                                    </MenuItem>
                                                );
                                            })}
                                        </TextField>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </StyledTableContainer>

            {previewData.length > 0 && (
                <PreviewContainer>
                    <Typography variant="subtitle1" gutterBottom>
                        Preview (first 3 rows)
                    </Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Currency</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Payment Method</TableCell>
                                <TableCell>Category ID</TableCell>
                                {previewData.some((d) => d.errors) && <TableCell>Errors</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.date.format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>{row.currency}</TableCell>
                                    <TableCell>{row.amount}</TableCell>
                                    <TableCell>{row.description}</TableCell>
                                    <TableCell>{row.paymentMethod || '-'}</TableCell>
                                    <TableCell>{row.categoryId || '-'}</TableCell>
                                    {previewData.some((d) => d.errors) && (
                                        <TableCell>
                                            {row.errors ? (
                                                <Typography variant="caption" color="error">
                                                    {row.errors.join(', ')}
                                                </Typography>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </PreviewContainer>
            )}

            {!requiredFieldsMapped && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    Please map all required fields (Date, Currency, Amount, Description) before continuing.
                </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!requiredFieldsMapped}
                >
                    Confirm Mapping
                </Button>
            </Box>
        </Box>
    );
};

